import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { customerCustomFetch, storeCustomerSessionId } from "@workspace/api-client-react";

export interface CustomerProfile {
  id: string;
  firstName: string;
  whatsappNumber: string;
  whatsappFormattedNumber: string | null;
  avatarBase64: string | null;
  birthDate: string | null;
  createdAt: string;
}

export interface CustomerLoginInput {
  whatsappNumber: string;
  ddi?: string;
  firstName?: string;
  birthDate?: string;
  /** Loja do link: com pontos ligados, quem já é cliente dela entra com código no WhatsApp. */
  storeSlug?: string;
}

export interface CustomerCodeChallenge {
  requestId: string;
  maskedNumber: string;
}

export interface CustomerLoginResult {
  customer: CustomerProfile | null;
  needsName: boolean;
  needsCode?: CustomerCodeChallenge;
  error?: string;
}

interface CustomerAuthContextValue {
  customer: CustomerProfile | null;
  /** Este aparelho confirmou o número no WhatsApp (só assim os pontos aparecem). */
  verified: boolean;
  isLoading: boolean;
  login: (input: CustomerLoginInput) => Promise<CustomerLoginResult>;
  verifyCode: (requestId: string, code: string) => Promise<{ customer?: CustomerProfile; error?: string }>;
  requestCode: (storeSlug?: string) => Promise<{ challenge?: CustomerCodeChallenge; alreadyVerified?: boolean; error?: string }>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

function apiError(err: any, fallback: string): string {
  return err?.data?.error?.message || err?.data?.message || fallback;
}

const CustomerAuthContext = createContext<CustomerAuthContextValue | null>(null);

export function CustomerAuthProvider({ children }: { children: ReactNode }) {
  const [customer, setCustomer] = useState<CustomerProfile | null>(null);
  const [verified, setVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await customerCustomFetch<{ customer: CustomerProfile; sessionId?: string; verified?: boolean }>(
        "/api/customer/auth/me",
      );
      if (res?.sessionId) storeCustomerSessionId(res.sessionId);
      setCustomer(res?.customer ?? null);
      setVerified(res?.verified === true);
    } catch {
      setCustomer(null);
      setVerified(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (input: CustomerLoginInput): Promise<CustomerLoginResult> => {
    try {
      const res = await customerCustomFetch<{
        customer?: CustomerProfile;
        needsName?: boolean;
        needsCode?: boolean;
        requestId?: string;
        maskedNumber?: string;
        sessionId?: string;
      }>("/api/customer/auth/login", {
        method: "POST",
        body: JSON.stringify(input),
      });
      if (res?.sessionId) storeCustomerSessionId(res.sessionId);
      if (res?.customer) {
        setCustomer(res.customer);
        return { customer: res.customer, needsName: false };
      }
      if (res?.needsCode && res.requestId) {
        return { customer: null, needsName: false, needsCode: { requestId: res.requestId, maskedNumber: res.maskedNumber ?? "" } };
      }
      return { customer: null, needsName: !!res?.needsName };
    } catch (err: any) {
      // Shape do api-server é { error: { message } } — checa aninhado primeiro.
      // err.message vem com prefixo "HTTP 4xx:" do custom-fetch, então é o
      // último recurso antes do fallback genérico.
      const message =
        err?.data?.error?.message ||
        err?.data?.message ||
        "Não foi possível validar seu WhatsApp. Tente novamente.";
      return { customer: null, needsName: false, error: message };
    }
  }, []);

  const verifyCode = useCallback(async (requestId: string, code: string) => {
    try {
      const res = await customerCustomFetch<{ customer?: CustomerProfile; sessionId?: string }>(
        "/api/customer/auth/verify-code",
        { method: "POST", body: JSON.stringify({ requestId, code }) },
      );
      if (res?.sessionId) storeCustomerSessionId(res.sessionId);
      if (res?.customer) {
        setCustomer(res.customer);
        setVerified(true);
        return { customer: res.customer };
      }
      return { error: "Não foi possível confirmar o código. Tente de novo." };
    } catch (err: any) {
      return { error: apiError(err, "Não foi possível confirmar o código. Tente de novo.") };
    }
  }, []);

  const requestCode = useCallback(async (storeSlug?: string) => {
    try {
      const res = await customerCustomFetch<{ requestId?: string; maskedNumber?: string; alreadyVerified?: boolean }>(
        "/api/customer/auth/request-code",
        { method: "POST", body: JSON.stringify(storeSlug ? { storeSlug } : {}) },
      );
      if (res?.alreadyVerified) {
        setVerified(true);
        return { alreadyVerified: true };
      }
      if (res?.requestId) return { challenge: { requestId: res.requestId, maskedNumber: res.maskedNumber ?? "" } };
      return { error: "Não foi possível enviar o código. Tente de novo." };
    } catch (err: any) {
      return { error: apiError(err, "Não foi possível enviar o código. Tente de novo.") };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await customerCustomFetch("/api/customer/auth/logout", { method: "POST" });
    } catch {
      // ignore
    }
    storeCustomerSessionId(null);
    setCustomer(null);
    setVerified(false);
  }, []);

  return (
    <CustomerAuthContext.Provider value={{ customer, verified, isLoading, login, verifyCode, requestCode, logout, refresh }}>
      {children}
    </CustomerAuthContext.Provider>
  );
}

export function useCustomerAuth(): CustomerAuthContextValue {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) {
    throw new Error("useCustomerAuth must be used within a CustomerAuthProvider");
  }
  return ctx;
}
